/* eslint-disable consistent-return */
import {
  Button,
  Grid,
  Checkbox,
  FormLabel,
} from '@material-ui/core';
import {
  updateBusinessContract
} from '@webapps/business';
import {
  FormikAutocompletePx,
  FormikDatePicker,
  FormikTextFieldPx,
  useAuth,
  useMutation,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { OPCO_CONTRACT_TYPE, DEROGATION_TYPE, BEST_DIPLOMA } from 'config/constants';

const BusinessContractForm = ({ businessContract, onChangeStepModal, onClose, refetch }) => {

  const [data, setData] = useState({
    businessContractLengths: businessContract?.businessContractLengths?.length > 0 ? businessContract?.businessContractLengths : [{year: 1, ageChange: false}] ,
    businessContractMasters: businessContract?.businessContractMasters || [{}],
    contractualMode: businessContract?.contractualMode || null,
    contractType: businessContract?.contractType || null,
    derogationType: businessContract?.derogationType || null,
    previousContract: businessContract?.previousContract || null,
    executionDate: businessContract?.executionDate || null,
    effectiveDate: businessContract?.effectiveDate || null,
    endDate: businessContract?.endDate || null,
    hebdoHours: businessContract?.hebdoHours || null,
    hebdoMinutes: businessContract?.hebdoMinutes || 0,
    dangerousWork: businessContract?.dangerousWork ? true : false,
    amendmentDate: businessContract?.amendmentDate || null,
    grossSalary: businessContract?.grossSalary || null,
    advantageFood: businessContract?.advantageFood || null,
    advantageAccommodation: businessContract?.advantageAccommodation || null,
    otherAdvantages: businessContract?.otherAdvantages ? true : false,
  });
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    onChangeStepModal(1);
  }, []);

  const {
    error,
    isLoading,
    isSuccess,
    mutateAsync: mutateUpdateBusinessContract,
  } = useMutation(updateBusinessContract);

  const { addToast } = useToast();

  useEffect(() => {
    if (error) {
      addToast({
        status: 'error',
        message: 'Erreur pour mettre à jour le contrat',
      });
    }
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Contrat mis à jour avec succès',
      });
      onClose();
      refetch();
    }
  }, [error, isSuccess]);

  const makeRequest = async (id, data) => {
    await mutateUpdateBusinessContract({
      id,
      data
    });
  };

  const handleNextStep = (newData, final = false) => {
    setData((prev) => ({ ...prev, ...newData }));

    if (final) {
      makeRequest(businessContract?.id , newData);
      return;
    }

    setCurrentStep((prev) => prev + 1);
    onChangeStepModal((prev) => prev + 1);
  };

  const handlePrevStep = (newData) => {
    setData((prev) => ({ ...prev, ...newData }));
    setCurrentStep((prev) => prev - 1);
    onChangeStepModal((prev) => prev - 1);
  };

  const steps = [
    <StepOne key={1} data={data} next={handleNextStep} />,
    <StepTwo key={2} data={data} next={handleNextStep} prev={handlePrevStep} />,
    <StepThree
      key={3}
      data={data}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
    <StepFour
      key={4}
      data={data}
      isLoading={isLoading}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
  ];

  return steps[currentStep];
};

BusinessContractForm.propTypes = {
  onClose: PropTypes.func,
  refetch: PropTypes.func,
  onChangeStepModal: PropTypes.func,
};

const stepOneValidationSchema = Yup.object({
  businessContractMasters: Yup.array().of(
    Yup.object({
      lastname: Yup.string().required('Requis'),
      firstname: Yup.string().required('Requis'),
      email: Yup.string().email('Email invalide').required('Requis'),
      occupiedPosition: Yup.string().required('Requis'),
      diplomaLabel: Yup.string().required('Requis'),
      bestDiploma: Yup.string().required('Requis'),
      birthDate: Yup.string().required('Requis'),
    })
  ),
  contractualMode: Yup.string().required('Requis'),
});

const StepOne = ({ data, next }) => {
  const {
    css,
    theme: { spacing, fontSizes },
  } = useStyles();
  
  const [refreshState, setRefreshState] = useState(false); // Sert pour refresh le composant

  const handleNewBusinessMaster = () => {
    if(formik.values?.businessContractMasters.length > 1) return;
    formik.values?.businessContractMasters.push({lastname: null, firstname: null, birthDate: null})
    setRefreshState(!refreshState);
  }

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepOneValidationSchema,
    onSubmit: (values) => {
      next(values);
    },
  });

  const formatDate = (e) => {
    return format(new Date(e), "yyyy-MM-dd'T'HH:mm:ss")
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12} sm={12}>
        <Grid item xs={12}>
          <FormikAutocompletePx
            formik={formik}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            onChange={(event, value) => {
              formik.setFieldValue('contractualMode', value?.value);
            }}
            label="Mode contractuel de l'apprentissage"
            name="contractualMode"
            options={CONTRACTUAL_MODE}
            value={CONTRACTUAL_MODE.find((option) => option?.value === formik.values.contractualMode) || null}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Nom du maître d'apprentissage"
            name="businessContractMasters[0].lastname"
            value={formik.values?.businessContractMasters[0]?.lastname}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Prénom du maître d'apprentissage"
            name="businessContractMasters[0].firstname"
            value={formik.values?.businessContractMasters[0]?.firstname}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Email du maître d'apprentissage"
            name="businessContractMasters[0].email"
            value={formik.values?.businessContractMasters[0]?.email}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikDatePicker
            formik={formik}
            fullWidth
            variant="outlined"
            label="Date de naissance du maître d'apprentissage"
            name="businessContractMasters[0].birthDate"
            onChange={(e) => formik.setFieldValue('businessContractMasters[0].birthDate', formatDate(e))}
            value={formik.values?.businessContractMasters[0]?.birthDate}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Emploi occupé du maître d'apprentissage"
            name="businessContractMasters[0].occupiedPosition"
            value={formik.values?.businessContractMasters[0]?.occupiedPosition}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Diplôme ou titre le plus élevé obtenu du maître d'apprentissage"	
            name="businessContractMasters[0].diplomaLabel"
            value={formik.values?.businessContractMasters[0]?.diplomaLabel}
          />
        </Grid>
        <Grid item xs={12}>
          <FormikAutocompletePx
            formik={formik}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            onChange={(event, value) => {
              formik.setFieldValue('businessContractMasters[0].bestDiploma', value?.value);
            }}
            label="Niveau de diplôme ou titre le plus élevé obtenu du maître d'apprentissage"
            name="businessContractMasters[0].bestDiploma"
            options={BEST_DIPLOMA}
            value={BEST_DIPLOMA.find((option) => option?.value === formik.values.businessContractMasters[0]?.bestDiploma) || null}
          />
        </Grid>
        {formik.values?.businessContractMasters.length > 1 ? (
          <>
            <Button
              color="secondary"
              onClick={() => {formik.values?.businessContractMasters.pop(); setRefreshState(!refreshState)}}
              variant="contained"
            >
              Supprimer le second maître d'apprentissage
            </Button>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikTextFieldPx
                formik={formik}
                fullWidth
                label="Nom du second maître d'apprentissage"
                name="businessContractMasters[1].lastname"
                value={formik.values?.businessContractMasters[1]?.lastname}
              />
            </Grid>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikTextFieldPx
                  formik={formik}
                  fullWidth
                  label="Prénom du second maître d'apprentissage"
                  name="businessContractMasters[1].firstname"
                  value={formik.values?.businessContractMasters[1]?.firstname}
                />
            </Grid>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikTextFieldPx
                formik={formik}
                fullWidth
                label="Email du maître d'apprentissage"
                name="businessContractMasters[1].email"
                value={formik.values?.businessContractMasters[1]?.email}
              />
            </Grid>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikDatePicker
                formik={formik}
                fullWidth
                variant="outlined"
                label="Date de second naissance du maître d'apprentissage"
                name="businessContractMasters[1].birthDate"
                onChange={(e) => formik.setFieldValue('businessContractMasters[1].birthDate', formatDate(e))}
                value={formik.values?.businessContractMasters[1]?.birthDate}
              />
            </Grid>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikTextFieldPx
                formik={formik}
                fullWidth
                label="Emploi occupé du maître d'apprentissage"
                name="businessContractMasters[1].occupiedPosition"
                value={formik.values?.businessContractMasters[1]?.occupiedPosition}
              />
            </Grid>
            <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
              <FormikTextFieldPx
                formik={formik}
                fullWidth
                label="Diplôme ou titre le plus élevé obtenu du maître d'apprentissage"	
                name="businessContractMasters[1].diplomaLabel"
                value={formik.values?.businessContractMasters[1]?.diplomaLabel}
              />
            </Grid>
            <Grid item xs={12}>
              <FormikAutocompletePx
                formik={formik}
                fullWidth
                getOptionLabel={(option) => option.label || ''}
                onChange={(event, value) => {
                  formik.setFieldValue('businessContractMasters[1].bestDiploma', value?.value);
                }}
                label="Niveau de diplôme ou titre le plus élevé obtenu du maître d'apprentissage"
                name="businessContractMasters[1].bestDiploma"
                options={BEST_DIPLOMA}
                value={BEST_DIPLOMA.find((option) => option?.value === formik.values.businessContractMasters[1]?.bestDiploma) || null}
              />
            </Grid>
          </> ) : (
            <Button
            color="primary"
            onClick={handleNewBusinessMaster}
            variant="contained"
            >
            Ajouter un second maître d'apprentissage
          </Button>
          )}          
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({ textAlign: 'center', marginTop: spacing(4) })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
            </Grid>
      </Grid>
    </div>
  );
};

StepOne.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
};

const stepTwoValidationSchema = Yup.object({
  contractType: Yup.string().required('Requis'),
});

const StepTwo = ({ data, next, prev }) => {
  const {
    css,
    theme: { fontSizes, spacing },
  } = useStyles();

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepTwoValidationSchema,
    onSubmit: (values) => next(values),
  });

  const formatDate = (e) => {
    return format(new Date(e), "yyyy-MM-dd'T'HH:mm:ss")
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
    <Grid container xs={12} sm={12}>
      <Grid item xs={12}>
        <FormikAutocompletePx
          formik={formik}
          fullWidth
          getOptionLabel={(option) => option.label || ''}
          onChange={(event, value) => {
            formik.setFieldValue('contractType', value?.value);
          }}
          label="Type de contrat ou d'avenant"
          name="contractType"
          options={OPCO_CONTRACT_TYPE}
          value={OPCO_CONTRACT_TYPE.find((option) => option?.value === formik.values.contractType) || null}
        />
      </Grid>
      <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
        <FormikAutocompletePx
          formik={formik}
          fullWidth
          getOptionLabel={(option) => option.label || ''}
          onChange={(event, value) => {
            formik.setFieldValue('derogationType', value?.value);
          }}
          label="Type de dérogation (si besoin)"
          name="derogationType"
          options={DEROGATION_TYPE}
          value={DEROGATION_TYPE.find((option) => option?.value === formik.values.derogationType) || null}
        />
      </Grid>
      <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
        <FormikTextFieldPx
          formik={formik}
          fullWidth
          label="Numéro du contrat ou avenant précédent (optionnel)"
          name="previousContract"
        />
      </Grid>
    </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
};

const stepThreeValidationSchema = Yup.object({
  executionDate: Yup.string().required('Requis'),
  effectiveDate: Yup.string().required('Requis'),
  endDate: Yup.string().required('Requis'),
});

const StepThree = ({ data, next, prev }) => {
  const {
    css,
    theme: { spacing, fontSizes },
  } = useStyles();


  const DANGEROUS_WORK = [
    { label: 'Oui', value: true },
    { label: 'Non', value: false },
  ];

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepThreeValidationSchema,
    onSubmit: (values) => next(values),
  });

  const formatDate = (e) => {
    return format(new Date(e), "yyyy-MM-dd'T'HH:mm:ss")
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12} sm={12}>
        <Grid item xs={12}>
          <FormikDatePicker
            formik={formik}
            fullWidth
            variant="outlined"
            label="Date de début d'exécution du contrat"
            name="executionDate"
            onChange={(e) => formik.setFieldValue('executionDate', formatDate(e))}
            value={formik.values.executionDate}
          />
        </Grid>
        <Grid item xs={12}>
          <FormikDatePicker
            formik={formik}
            fullWidth
            variant="outlined"
            label="Date de début de formation pratique chez l'employeur"
            name="effectiveDate"
            onChange={(e) => formik.setFieldValue('effectiveDate', formatDate(e))}
            value={formik.values.effectiveDate}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikDatePicker
            formik={formik}
            fullWidth
            variant="outlined"
            label="Date de fin du contrat ou apprentissage"
            name="endDate"
            onChange={(e) => formik.setFieldValue('endDate', formatDate(e))}
            value={formik.values.endDate}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikDatePicker
            formik={formik}
            fullWidth
            variant="outlined"
            label="Si avenant, date d'effet"
            name="amendmentDate"
            onChange={(e) => formik.setFieldValue('amendmentDate', formatDate(e))}
            value={formik.values.amendmentDate}
          />
        </Grid>
        <div className={css({ marginTop: spacing(3) })}>Temps de travail hebdomadaire</div>
        <Grid item xs={6}>
          <FormikTextFieldPx
            formik={formik}
            defaultValue={0}
            fullWidth
            type="number"
            label="heures"
            name="hebdoHours"
            value={formik.values.hebdoHours}
          />
        </Grid>
        <Grid item xs={6}>
          <FormikTextFieldPx
            formik={formik}
            defaultValue={0}
            fullWidth
            type="number"
            label="minutes"
            name="hebdoMinutes"
            value={formik.values.hebdoMinutes}
          />
        </Grid>
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          <FormikAutocompletePx
            formik={formik}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            onChange={(event, value) => {
              formik.setFieldValue('dangerousWork', value?.value);
            }}
            label="Travail exposé à des risques particuliers (Machines dangereuses, produits toxiques, etc.)"
            name="dangerousWork"
            options={DANGEROUS_WORK}
            value={DANGEROUS_WORK.find((option) => option?.value === formik.values.dangerousWork) || null}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepThree.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
};

const StepFour = ({ data, isLoading, next, prev }) => {
  const {
    classes,
    css,
    theme: { spacing, fontSizes },
  } = useStyles(styles);

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    onSubmit: (values) => {
      next(values, true);
    },
  });

  const TYPE = [
    { label: 'SMIC', value: 'SMIC' },
    { label: 'SMC', value: 'SMC' },
  ]



  const getNextYear = () => {
    const year = formik.values.businessContractLengths.sort((a, b) => a.year - b.year);
    return year[year.length - 1].year + 1;
  };

  const getAgeChange = () => {
    return formik.values.businessContractLengths[formik.values.businessContractLengths.length - 1].ageChange;
  };

  
  const handleAddBusinessContractLength = () => {
    const year = getNextYear();
    if(year > 4) return;
    formik.setFieldValue('businessContractLengths', [
      ...formik.values.businessContractLengths,
      { startDate: '', endDate: '', type: '', percentage: '', year, ageChange: false },
    ]);
  };
  const handleAddBusinessContractLengthWithAgeChange = () => {
    const year = getNextYear();
    formik.setFieldValue('businessContractLengths', [
      ...formik.values.businessContractLengths,
      { startDate: '', endDate: '', type: '', percentage: '', year: year - 1, ageChange: true },
    ]);
  };

  const handleRemoveBusinessContractLength = () => {
    if (formik.values.businessContractLengths.length > 1) {
      formik.setFieldValue('businessContractLengths', 
        formik.values.businessContractLengths.slice(0, -1)
      );
    }
  };
  const formatDate = (e) => {
    return format(new Date(e), "yyyy-MM-dd'T'HH:mm:ss")
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': {
          margin: spacing(0),
          marginTop: spacing(1)
        },
      })}
    >
      <Grid container xs={12} sm={12}>
        <Grid item xs={12}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Salaire à l'embauche (brut)"
            type="number"
            name="grossSalary"
          />
        </Grid>
        <Grid item xs={12}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Avantage en nature nourriture"
            type="number"
            name="advantageFood"
          />
        </Grid>
        <Grid item xs={12}>
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            label="Avantage en nature logement"
            type="number"
            name="advantageAccommodation"
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel style={{ color: '#152652', alignSelf: 'center' }}>Autre avantages</FormLabel>
            <Checkbox
              className={css({ '.MuiIconButton-label': { color: '#152652 !important' } })}
              checked={formik.values.otherAdvantages}
              name="otherAdvantages"
              onChange={formik.handleChange}
              size="small"
            />
        </Grid>
        {formik.values.businessContractLengths.map((contract, index) => (
        <>
          <div>Année {contract.year} {contract.ageChange && "(Avec changement d'âge)"}</div>
          <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
            <FormikDatePicker
              formik={formik}
              fullWidth
              variant="outlined"
              label={`Date de début année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
              name={`businessContractLengths[${index}].startDate`}
              onChange={(e) => formik.setFieldValue(`businessContractLengths[${index}].startDate`, formatDate(e))}
              value={contract.startDate}
            />
          </Grid>
          <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
            <FormikDatePicker
              formik={formik}
              fullWidth
              variant="outlined"
              label={`Date de fin année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
              name={`businessContractLengths[${index}].endDate`}
              onChange={(e) => formik.setFieldValue(`businessContractLengths[${index}].endDate`, formatDate(e))}
              value={contract.endDate}
            />
          </Grid>
          <Grid item xs={6} className={css({ marginTop: spacing(3) })}>
            <FormikAutocompletePx
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              onChange={(event, value) => {
                formik.setFieldValue(`businessContractLengths[${index}].type`, value?.value);
              }}
              label={`Type de salaire année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
              name={`businessContractLengths[${index}].type`}
              options={TYPE}
              value={TYPE.find((option) => option?.value === contract.type) || false}
            />
          </Grid>
          <Grid item xs={6} className={css({ marginTop: spacing(3) })}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label={`Pourcentage de salaire année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
              name={`businessContractLengths[${index}].percentage`}
              type="number"
              value={contract.percentage}
            />
          </Grid>
        </>
        ))}
        <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
          {getNextYear() < 5 && (
            <Button
              color="primary"
              onClick={handleAddBusinessContractLength}
              variant="contained"
            >
              Ajouter une nouvelle ligne pour l'année {getNextYear()}
            </Button>
          )}
          {!getAgeChange() && (
            <Button
              color="primary"
              onClick={handleAddBusinessContractLengthWithAgeChange}
              variant="contained"
            >
              Ajouter une nouvelle ligne changement d'âge qui conduit à un changement de grille tarifaire
            </Button>
          )}
          {formik.values.businessContractLengths.length > 1 && (
            <Button
              color="secondary"
              onClick={handleRemoveBusinessContractLength}
              variant="contained"
            >
              Supprimer la dernière ligne ajoutée
            </Button>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 20 })}
            color="primary"
            disabled={isLoading}
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Enregistrer
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

const styles = ({ fontSizes }) => ({
  countAspect: {
    color: 'black',
    fontSize: fontSizes(16),
    '@media (max-width:1380px)': { fontSize: fontSizes(15) },
    '@media (max-width:1278px)': { fontSize: fontSizes(14), },
    '@media (max-width:1227px)': { fontSize: fontSizes(13), },
    '@media (max-width:1020px)': { fontSize: fontSizes(16), },
  },
  countAspectPassed: {
    color: 'red',
    fontSize: fontSizes(16),
    '@media (max-width:1380px)': { fontSize: fontSizes(15) },
    '@media (max-width:1278px)': { fontSize: fontSizes(14), },
    '@media (max-width:1227px)': { fontSize: fontSizes(13), },
    '@media (max-width:1020px)': { fontSize: fontSizes(16), },
  }
});

StepFour.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default BusinessContractForm;
